import * as React from "react";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import Loading from "../../components/loading";
import {
    Alert,
    FormControl,
    FormControlLabel,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableSortLabel,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import TableHeaderRow from "../../components/TableHeaderRow";
import TableFooterRow from "../../components/TableFooterRow";
import MultiSelect from "../../components/multiSelect/multiSelect";
import useDebounce from "../../lib/UseDebounce";
import {useAuth} from "../../providers/auth";
import {PermissionType} from "../../objects/UserRead";
import {toast} from "react-toastify";
import moment from "moment";
import {fetchCompaniesActive} from "../campaigns/campaignsService";
import {searchBrokers as searchBrokerOptions} from "../blockedPublishers/blockedPublishersService";
import {searchPublishers} from "../publishers/publishersService";
import {
    downloadPrCampaignsCsv,
    permaDeletePr,
    searchPortalRedirectCampaigns,
    setPrActive,
    setPrExclusive,
    setPrExclusiveOnly,
    togglePrLb,
    zeroPrDailyCap,
} from "./portalRedirectCampaignsService";
import PrUpsertModal from "./prUpsertModal";
import PrCloneModal from "./prCloneModal";
import PrCommentsModal from "./prCommentsModal";
import PrSkipStatsModal from "./prSkipStatsModal";
import PrCheckerModal from "./prCheckerModal";
import PrConfirmDialog from "./prConfirmDialog";
import {EXPAND_CELL, PrCampaignBlock} from "./prCampaignRow";

const VIEW_ITEMS = ["All", "Exclusive", "Non-Exclusive", "Exclusive Only", "Exclusive Paused", "Linkout Campaigns", "HP Campaigns", "1+ PR Lead"];
const TEXT_SORT = new Set(["companyName", "brokerName", "schoolName", "appId"]);

function money(n: number, digits = 2) {
    return "$" + (n || 0).toLocaleString(undefined, {minimumFractionDigits: digits, maximumFractionDigits: digits});
}

function compareRows(a: any, b: any, column: string, asc: boolean) {
    const av = a[column] ?? 0;
    const bv = b[column] ?? 0;
    if (av < bv) {
        return asc ? -1 : 1;
    }
    if (av > bv) {
        return asc ? 1 : -1;
    }
    return 0;
}

function groupKey(r: any) {
    return `${r.companyName}${r.brokerName}${r.schoolName}`;
}

function matchesQuery(row: any, q: string) {
    const search = q.trim().toLowerCase();
    if (!search) {
        return true;
    }
    const hay = [
        row.companyName, row.brokerName, row.schoolName, row.appId,
        row.hpCampaignId, row.hpCampaignIdLb, row.hpCampaignIdLbTemp, row.id,
    ].map((v) => String(v ?? "").toLowerCase()).join(" ");
    const op = search.includes(" and ") ? "and" : "or";
    const terms = search.split(op === "and" ? " and " : " or ").map((t) => t.trim()).filter(Boolean);
    if (!terms.length) {
        return true;
    }
    return op === "and" ? terms.every((t) => hay.includes(t)) : terms.some((t) => hay.includes(t));
}

function blankChecker() {
    return {on: false, url: "", body: "", headers: "", resolutions: []};
}

const CELL_PX = "3px";
const SEARCH_DEBOUNCE_MS = 300;

const PrSearchBoxes = React.memo(function PrSearchBoxes({onSearch, onRefine, onSearchTyped}: {onSearch: (value: string) => void; onRefine: (value: string) => void; onSearchTyped: (value: string) => void}) {
    const [searchText, setSearchText] = React.useState("");
    const [refineText, setRefineText] = React.useState("");

    useDebounce(() => {
        onSearch(searchText);
    }, [searchText, onSearch], searchText.trim() ? SEARCH_DEBOUNCE_MS : 0);

    useDebounce(() => {
        onRefine(refineText);
    }, [refineText, onRefine], refineText.trim() ? SEARCH_DEBOUNCE_MS : 0);

    return (
        <>
            <Input id="search" name="search" type="search" autoComplete="on" size="small" placeholder="Search" value={searchText} onChange={(e) => { const v = e.target.value; setSearchText(v); onSearchTyped(v); if (!v.trim()) { setRefineText(""); } }} startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>} sx={{minWidth: 260}}/>
            <Input id="search-refine" name="search" type="search" autoComplete="on" size="small" placeholder="Refine" value={refineText} onChange={(e) => setRefineText(e.target.value)} disabled={!searchText.trim()} startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>} sx={{minWidth: 260}}/>
        </>
    );
});

export default function PortalRedirectCampaignsPage() {
    const auth = useAuth();
    const isAdmin = !!auth.user?.permissions?.includes(PermissionType.Admin);
    const [dateRange, setDateRange] = React.useState<any>([]);
    const [active, setActive] = React.useState(true);
    const [live, setLive] = React.useState(true);
    const [viewSelected, setViewSelected] = React.useState("All");
    const [searchQuery, setSearchQuery] = React.useState("");
    const [refineQuery, setRefineQuery] = React.useState("");
    const searchRef = React.useRef("");
    const loadGen = React.useRef(0);
    const [loading, setLoading] = React.useState(false);
    const [rows, setRows] = React.useState<any[]>([]);
    const [grouped, setGrouped] = React.useState<any[]>([]);
    const [openAll, setOpenAll] = React.useState(false);
    const [openKeys, setOpenKeys] = React.useState<Set<string>>(() => new Set());
    const [sort, setSort] = React.useState({column: "cpi", asc: false});
    const [selectedCompanies, setSelectedCompanies] = React.useState<any[]>([]);
    const [selectedPublishers, setSelectedPublishers] = React.useState<any[]>([]);
    const [selectedBrokers, setSelectedBrokers] = React.useState<any[]>([]);
    const [menu, setMenu] = React.useState<{row: any; el: HTMLElement} | null>(null);
    const [upsertRow, setUpsertRow] = React.useState<any>(null);
    const [cloneRow, setCloneRow] = React.useState<any>(null);
    const [commentRow, setCommentRow] = React.useState<any>(null);
    const [checkerRow, setCheckerRow] = React.useState<any>(null);
    const [checkerKind, setCheckerKind] = React.useState<"dupe" | "score">("dupe");
    const [skipId, setSkipId] = React.useState<number | null>(null);
    const [pending, setPending] = React.useState<{message: string; fn: () => Promise<boolean>; success: string} | null>(null);
    const [pendingLoading, setPendingLoading] = React.useState(false);

    React.useEffect(() => {
        document.title = "Portal Redirect | Campaigns";
        setDateRange(RangeToTimes("Today"));
    }, []);

    const canShowMonthly = !dateRange?.[0] || moment(dateRange[0]).format("MM") === moment().format("MM");
    const isToday = dateRange?.[0] && moment(dateRange[0]).isSame(moment(), "day") && moment(dateRange[1]).isSame(moment(), "day");

    const buildInput = (date = dateRange) => {
        const input: any = {
            startDate: moment(date[0]).format("YYYY-MM-DD HH:mm:ss"),
            endDate: moment(date[1]).format("YYYY-MM-DD HH:mm:ss"),
            active,
            live,
            search: (searchRef.current || searchQuery).trim() || undefined,
            companyIds: selectedCompanies.length ? selectedCompanies.map((c: any) => c.id) : undefined,
            publisherIds: selectedPublishers.length ? selectedPublishers.map((c: any) => c.id) : undefined,
            brokerIds: selectedBrokers.length ? selectedBrokers.map((c: any) => c.id) : undefined,
        };
        if (viewSelected === "Linkout Campaigns") {
            input.redirectType = "Linked";
        } else if (viewSelected === "HP Campaigns") {
            input.redirectType = "Hosted";
        } else if (viewSelected === "Exclusive") {
            input.isExclusive = true;
        } else if (viewSelected === "Exclusive Only") {
            input.isExclusiveOnly = true;
        } else if (viewSelected === "Exclusive Paused") {
            input.isExclusiveTemp = true;
        } else if (viewSelected === "1+ PR Lead") {
            input.oneLead = true;
        } else if (viewSelected === "Non-Exclusive") {
            input.isNonExclusive = true;
        }
        return input;
    };

    const applyGroups = (data: any[]) => {
        const map: Record<string, any[]> = {};
        data.forEach((r) => {
            const k = groupKey(r);
            if (!map[k]) {
                map[k] = [];
            }
            map[k].push(r);
        });
        const next: any[] = [];
        Object.keys(map).forEach((k) => {
            const list = map[k];
            if (list.length === 1) {
                next.push({...list[0], group: false, key: k});
                return;
            }
            const gr: any = {
                group: true,
                key: k,
                companyName: list[0].companyName,
                brokerName: list[0].brokerName,
                schoolName: list[0].schoolName,
                isBranded: false,
                isExclusive: false,
                isExclusiveTemp: false,
                isExclusiveOnly: false,
                activeFlag: true,
                impressions: 0, skip: 0, skipPercent: 0, leads: 0, leadsLb: 0, leadsTotal: 0, revenue: 0,
                dailyCap: 0, monthlyCap: 0, monthly: 0, monthlyLb: 0, monthlyTotal: 0, ctr: 0, cpi: 0,
                children: list,
                dupeChecker: blankChecker(),
                scoreChecker: blankChecker(),
                whenStart: [],
            };
            let cpiRev = 0;
            let cpiImp = 0;
            list.forEach((sr) => {
                gr.impressions += sr.impressions;
                gr.skip += sr.skip;
                gr.leads += sr.leads;
                gr.leadsLb += sr.leadsLb;
                gr.leadsTotal += sr.leadsTotal;
                gr.revenue += sr.revenue;
                gr.dailyCap += sr.dailyCap;
                gr.monthlyCap += sr.monthlyCap;
                gr.monthly += sr.monthly;
                gr.monthlyLb += sr.monthlyLb;
                gr.monthlyTotal += sr.monthlyTotal;
                if (sr.isBranded) {
                    cpiRev += sr.revenue;
                    cpiImp += sr.impressions;
                } else {
                    cpiRev += sr.cpi * (sr.impressions + sr.leadsLb);
                    cpiImp += sr.impressions + sr.leadsLb;
                }
            });
            if (gr.impressions > 0) {
                gr.ctr = gr.leads / gr.impressions * 100;
                gr.cpi = cpiImp ? cpiRev / cpiImp : 0;
                gr.skipPercent = gr.skip / (gr.impressions + gr.skip) * 100;
            }
            next.push(gr);
        });
        return next;
    };

    const load = async (date = dateRange) => {
        if (!date?.length) {
            return;
        }
        const gen = ++loadGen.current;
        setLoading(true);
        try {
            const data = await searchPortalRedirectCampaigns(buildInput(date));
            if (gen !== loadGen.current) {
                return;
            }
            if (data == null) {
                toast.error("Failed to load campaigns. Keep Live on or narrow the search.");
                return;
            }
            const nextGrouped = applyGroups(data);
            setRows(data);
            setGrouped(nextGrouped);
            if (openAll) {
                setOpenKeys(new Set(nextGrouped.filter((r) => r.group).map((r) => r.key)));
            }
        } catch (e) {
            if (gen !== loadGen.current) {
                return;
            }
            toast.error("Failed to load campaigns. Keep Live on or narrow the search.");
        } finally {
            if (gen === loadGen.current) {
                setLoading(false);
            }
        }
    };

    React.useEffect(() => {
        if (dateRange.length) {
            load(dateRange);
        }
    }, [dateRange, active, live, viewSelected, selectedCompanies, selectedPublishers, selectedBrokers]);

    const doSort = (column: string) => {
        if (sort.column === column) {
            setSort({column, asc: !sort.asc});
        } else {
            setSort({column, asc: TEXT_SORT.has(column)});
        }
    };

    const onSearch = React.useCallback((value: string) => {
        React.startTransition(() => setSearchQuery(value));
    }, []);

    const onSearchTyped = React.useCallback((value: string) => {
        searchRef.current = value;
    }, []);

    const onRefine = React.useCallback((value: string) => {
        React.startTransition(() => setRefineQuery(value));
    }, []);

    const filteredRows = React.useMemo(() => {
        const search = searchQuery.trim();
        if (!search) {
            return rows;
        }
        let next = rows.filter((r) => matchesQuery(r, search));
        const refine = refineQuery.trim();
        if (refine) {
            next = next.filter((r) => matchesQuery(r, refine));
        }
        return next;
    }, [rows, searchQuery, refineQuery]);

    const displayGrouped = React.useMemo(() => {
        if (!searchQuery.trim()) {
            return grouped;
        }
        return applyGroups(filteredRows);
    }, [grouped, filteredRows, searchQuery]);

    const sorted = React.useMemo(() => {
        const copy = [...displayGrouped];
        copy.sort((a, b) => compareRows(a, b, sort.column, sort.asc));
        return copy;
    }, [displayGrouped, sort]);

    const childrenByKey = React.useMemo(() => {
        const map: Record<string, any[]> = {};
        displayGrouped.forEach((row) => {
            if (row.group && row.children) {
                map[row.key] = [...row.children].sort((a, b) => compareRows(a, b, sort.column, sort.asc));
            }
        });
        return map;
    }, [displayGrouped, sort]);

    const toggleOpen = React.useCallback((key: string) => {
        setOpenKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const onMenu = React.useCallback((row: any, el: HTMLElement) => {
        setMenu({row, el});
    }, []);

    const onSkip = React.useCallback((hpCampaignId: number) => {
        setSkipId(hpCampaignId);
    }, []);

    const toggleOpenAll = () => {
        const next = !openAll;
        setOpenAll(next);
        if (next) {
            setOpenKeys(new Set(displayGrouped.filter((r) => r.group).map((r) => r.key)));
        } else {
            setOpenKeys(new Set());
        }
    };

    const totals = React.useMemo(() => {
        const t = {impressions: 0, leads: 0, leadsLb: 0, leadsTotal: 0, revenue: 0, monthly: 0, monthlyLb: 0, monthlyTotal: 0, ctr: 0, cpi: 0};
        sorted.forEach((row) => {
            t.impressions += row.impressions || 0;
            t.leads += row.leads || 0;
            t.leadsLb += row.leadsLb || 0;
            t.leadsTotal += row.leadsTotal || 0;
            t.revenue += row.revenue || 0;
            t.monthly += row.monthly || 0;
            t.monthlyLb += row.monthlyLb || 0;
            t.monthlyTotal += row.monthlyTotal || 0;
        });
        if (t.impressions > 0) {
            t.ctr = t.leads / t.impressions * 100;
            t.cpi = t.revenue / t.impressions;
        }
        return t;
    }, [sorted]);

    const searchCompanies = async (search: string, selected: any[]) => {
        const ids = new Set(selected.map((x: any) => x.id));
        const q = (search || "").toLowerCase();
        const list = await fetchCompaniesActive();
        return list
            .filter((c: any) => !ids.has(c.id) && (!q || (c.name || "").toLowerCase().includes(q)))
            .map((c: any) => ({id: c.id, value: c.name}));
    };

    const searchPrPublishers = async (search: string, selected: any[]) => {
        const ids = new Set(selected.map((x: any) => x.id));
        const res = await searchPublishers(true, 500, search, "name", true);
        return (res.publishers || [])
            .filter((p: any) => p.quality === 100 && !ids.has(p.id))
            .map((p: any) => ({id: p.id, value: `${p.name} (${p.id})`}));
    };

    const confirmAct = (message: string, fn: () => Promise<boolean>, success: string) => {
        setMenu(null);
        setPending({message, fn, success});
    };

    const runPending = async () => {
        if (!pending) {
            return;
        }
        setPendingLoading(true);
        const ok = await pending.fn();
        if (ok) {
            toast.success(pending.success);
            load();
            setPending(null);
        }
        setPendingLoading(false);
    };

    const rowLabel = (row: any) => `${row.brokerName} > ${row.schoolName} - ${row.appId}`;

    const sortLabel = (column: string, text: string) => (
        <TableSortLabel active={sort.column === column} direction={sort.column === column && sort.asc ? "asc" : "desc"} onClick={() => doSort(column)} sx={{userSelect: "text"}}>
            {text}
        </TableSortLabel>
    );

    const startDateStr = dateRange[0] ? moment(dateRange[0]).format("YYYY-MM-DD HH:mm:ss") : "";
    const endDateStr = dateRange[1] ? moment(dateRange[1]).format("YYYY-MM-DD HH:mm:ss") : "";

    return (
        <>
            <h2>Portal Redirect Campaigns</h2>
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1, mb: 1}}>
                <DateRange showTime={true} dateRange={dateRange} setDateRange={setDateRange} style={{marginRight: 8}}/>
                {isAdmin && (
                    <>
                        <Box sx={{width: 160}}><MultiSelect searchFunction={searchCompanies} selectedItems={selectedCompanies} setSelectedItems={setSelectedCompanies} placeholder="Company"/></Box>
                        <Box sx={{width: 160}}><MultiSelect searchFunction={searchPrPublishers} selectedItems={selectedPublishers} setSelectedItems={setSelectedPublishers} placeholder="Pub"/></Box>
                        <Box sx={{width: 160}}><MultiSelect searchFunction={searchBrokerOptions} selectedItems={selectedBrokers} setSelectedItems={setSelectedBrokers} placeholder="Broker"/></Box>
                        <FormControl size="small" sx={{minWidth: 160}}>
                            <InputLabel>Type</InputLabel>
                            <Select label="Type" value={viewSelected} onChange={(e) => setViewSelected(String(e.target.value))}>
                                {VIEW_ITEMS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </>
                )}
                <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} size="small"/>} label="Active"/>
                <FormControlLabel control={<Switch checked={live} onChange={(e) => setLive(e.target.checked)} size="small"/>} label="Live"/>
                <Button variant="contained" color="success" onClick={() => load()}>Run</Button>
                {isAdmin && <IconButton onClick={() => downloadPrCampaignsCsv(filteredRows)} disabled={!filteredRows.length} title="Download"><FileDownloadIcon/></IconButton>}
                {isAdmin && <Button variant="contained" color="warning" onClick={() => setUpsertRow("create")}>Create Campaign</Button>}
                <PrSearchBoxes onSearch={onSearch} onRefine={onRefine} onSearchTyped={onSearchTyped}/>
            </Box>

            {loading || !dateRange.length ? <Loading/> : !sorted.length ? <Alert severity="warning">No results found</Alert> : (
                <TableContainer component={Paper} sx={{maxHeight: "calc(100vh - 220px)", maxWidth: "calc(100vw - 10px)"}}>
                    <Table size="small" stickyHeader sx={{
                        fontSize: 12,
                        "& .MuiTableCell-root": {px: CELL_PX, py: "4px", borderRight: "1px solid #ddd"},
                        "& thead .MuiTableCell-root": {fontSize: 11.5, userSelect: "text"},
                        "& tbody .MuiTableCell-root": {py: "3px"},
                    }}>
                        <TableHead sx={{userSelect: "text", "& th": {userSelect: "text"}}}>
                            <TableHeaderRow>
                                <TableCell sx={EXPAND_CELL}>
                                    <IconButton size="small" sx={{p: 0}} onClick={toggleOpenAll}>
                                        {openAll ? <IndeterminateCheckBoxOutlinedIcon sx={{fontSize: 18}}/> : <AddBoxOutlinedIcon sx={{fontSize: 18}}/>}
                                    </IconButton>
                                </TableCell>
                                <TableCell>{sortLabel("companyName", "Cmp")}</TableCell>
                                <TableCell>{sortLabel("brokerName", "Broker")}</TableCell>
                                <TableCell>{sortLabel("schoolName", "School")}</TableCell>
                                <TableCell>{sortLabel("appId", "App Id")}</TableCell>
                                <TableCell>{sortLabel("hpCampaignId", "SF Id")}</TableCell>
                                {isAdmin && <TableCell>{sortLabel("hpCampaignIdLb", "LB Id")}</TableCell>}
                                <TableCell>{sortLabel("cplSf", "CPL SF")}</TableCell>
                                {isAdmin && <TableCell>{sortLabel("cplLb", "CPL LB")}</TableCell>}
                                {isAdmin && <TableCell>{sortLabel("grade", "Gr SF")}</TableCell>}
                                {isAdmin && <TableCell>{sortLabel("gradeLb", "Gr LB")}</TableCell>}
                                {isAdmin && <TableCell>{sortLabel("impressions", "Imp")}</TableCell>}
                                {isAdmin && <TableCell>{sortLabel("leads", "Leads PR")}</TableCell>}
                                {isAdmin && <TableCell>{sortLabel("leadsLb", "Leads LB")}</TableCell>}
                                <TableCell>{sortLabel("revenue", "Rev")}</TableCell>
                                {isAdmin && <TableCell>{sortLabel("ctr", "CONV %")}</TableCell>}
                                {isAdmin && <TableCell>{sortLabel("cpi", "EPI")}</TableCell>}
                                <TableCell>{sortLabel("leadsTotal", "Leads Range")}</TableCell>
                                {canShowMonthly && <TableCell>{sortLabel("dailyCap", "D. Cap")}</TableCell>}
                                {canShowMonthly && <TableCell>{sortLabel("monthlyCap", "M. Cap")}</TableCell>}
                                {isAdmin && canShowMonthly && <TableCell>{sortLabel("monthly", "M. PR")}</TableCell>}
                                {isAdmin && canShowMonthly && <TableCell>{sortLabel("monthlyLb", "M. LB")}</TableCell>}
                                {canShowMonthly && <TableCell>{sortLabel("monthlyTotal", "M. Tot")}</TableCell>}
                                {isAdmin && <TableCell>{sortLabel("skipPercent", "Skip")}</TableCell>}
                                {isAdmin && <TableCell/>}
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((row) => (
                                <PrCampaignBlock
                                    key={row.group ? row.key : row.id}
                                    row={row}
                                    open={!!row.group && openKeys.has(row.key)}
                                    childRows={row.group ? childrenByKey[row.key] : undefined}
                                    isAdmin={isAdmin}
                                    canShowMonthly={canShowMonthly}
                                    isToday={!!isToday}
                                    onToggle={toggleOpen}
                                    onMenu={onMenu}
                                    onSkip={onSkip}
                                />
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableFooterRow>
                                <TableCell colSpan={isAdmin ? 11 : 7}>Totals/Averages:</TableCell>
                                {isAdmin && <TableCell align="right">{totals.impressions.toLocaleString()}</TableCell>}
                                {isAdmin && <TableCell align="right">{totals.leads.toLocaleString()}</TableCell>}
                                {isAdmin && <TableCell align="right">{totals.leadsLb.toLocaleString()}</TableCell>}
                                <TableCell align="right">{money(totals.revenue, 0)}</TableCell>
                                {isAdmin && <TableCell align="right">{totals.ctr.toFixed(1)}%</TableCell>}
                                {isAdmin && <TableCell align="right">{money(totals.cpi)}</TableCell>}
                                <TableCell align="right">{totals.leadsTotal.toLocaleString()}</TableCell>
                                {canShowMonthly && <TableCell/>}
                                {canShowMonthly && <TableCell/>}
                                {isAdmin && canShowMonthly && <TableCell align="right">{totals.monthly.toLocaleString()}</TableCell>}
                                {isAdmin && canShowMonthly && <TableCell align="right">{totals.monthlyLb.toLocaleString()}</TableCell>}
                                {canShowMonthly && <TableCell align="right">{totals.monthlyTotal.toLocaleString()}</TableCell>}
                                {isAdmin && <TableCell/>}
                                {isAdmin && <TableCell/>}
                            </TableFooterRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}

            <PrUpsertModal row={upsertRow} onClose={() => setUpsertRow(null)} onSaved={() => { setUpsertRow(null); load(); }}/>
            <PrCloneModal row={cloneRow} onClose={() => setCloneRow(null)} onSaved={() => { setCloneRow(null); load(); }}/>
            <PrCommentsModal row={commentRow} onClose={() => setCommentRow(null)}/>
            <PrSkipStatsModal hpCampaignId={skipId} startDate={startDateStr} endDate={endDateStr} onClose={() => setSkipId(null)}/>
            <PrCheckerModal row={checkerRow} kind={checkerKind} onClose={() => setCheckerRow(null)} onSaved={() => { setCheckerRow(null); load(); }}/>
            {menu && (
                <Menu open={true} anchorEl={menu.el} onClose={() => setMenu(null)}>
                    {!!menu.row.dailyCap && <MenuItem onClick={() => confirmAct(`Are you sure you want to zero Daily Cap for ${rowLabel(menu.row)}?`, () => zeroPrDailyCap(menu.row.id), `${rowLabel(menu.row)} now has a zero Daily Cap.`)}>Zero Daily Cap</MenuItem>}
                    {!menu.row.activeFlag && <MenuItem onClick={() => confirmAct(`Are you sure you want to activate ${rowLabel(menu.row)}?`, () => setPrActive(menu.row.id, true), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now Active.`)}>Activate</MenuItem>}
                    {menu.row.activeFlag && <MenuItem onClick={() => confirmAct(`Are you sure you want to de-activate ${rowLabel(menu.row)}?`, () => setPrActive(menu.row.id, false), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now Inactive.`)}>De-Activate</MenuItem>}
                    {menu.row.isExclusive && <MenuItem onClick={() => confirmAct(`Are you sure you want to remove Exclusive from ${rowLabel(menu.row)}?`, () => setPrExclusive(menu.row.id, false), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now non-Exclusive.`)}>Remove Exclusive</MenuItem>}
                    {!menu.row.isExclusive && <MenuItem onClick={() => confirmAct(`Are you sure you want to mark Exclusive ${rowLabel(menu.row)}?`, () => setPrExclusive(menu.row.id, true), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now Exclusive.`)}>Mark Exclusive</MenuItem>}
                    {menu.row.isExclusiveOnly && <MenuItem onClick={() => confirmAct(`Are you sure you want to remove Exclusive Only from ${rowLabel(menu.row)}?`, () => setPrExclusiveOnly(menu.row.id, false), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now non-Exclusive Only.`)}>Remove Exclusive Only</MenuItem>}
                    {!menu.row.isExclusiveOnly && <MenuItem onClick={() => confirmAct(`Are you sure you want to mark Exclusive Only ${rowLabel(menu.row)}?`, () => setPrExclusiveOnly(menu.row.id, true), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now Exclusive Only.`)}>Mark Exclusive Only</MenuItem>}
                    {menu.row.hpCampaignIdLb && !menu.row.hpCampaignIdLbTemp && <MenuItem onClick={() => confirmAct(`Are you sure you want to LB Pause ${rowLabel(menu.row)}?`, () => togglePrLb(menu.row.id), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now LB Paused.`)}>LB Pause</MenuItem>}
                    {menu.row.hpCampaignIdLbTemp && <MenuItem onClick={() => confirmAct(`Are you sure you want to LB Live ${rowLabel(menu.row)}?`, () => togglePrLb(menu.row.id), `${menu.row.schoolName} - ${menu.row.appId} (${menu.row.hpCampaignId}) is now LB Live.`)}>LB Live</MenuItem>}
                    <MenuItem onClick={() => { setMenu(null); setCheckerKind("dupe"); setCheckerRow(menu.row); }}>Dupe Checker</MenuItem>
                    <MenuItem onClick={() => { setMenu(null); setCheckerKind("score"); setCheckerRow(menu.row); }}>Score Checker</MenuItem>
                    <MenuItem onClick={() => { setMenu(null); setCommentRow(menu.row); }}>Comments</MenuItem>
                    <MenuItem onClick={() => { setMenu(null); setUpsertRow(menu.row); }}>Edit Campaign</MenuItem>
                    <MenuItem onClick={() => { setMenu(null); setCloneRow(menu.row); }}>Clone Campaign</MenuItem>
                    {!menu.row.activeFlag && <MenuItem onClick={() => confirmAct(`Are you sure you want to perma-delete ${rowLabel(menu.row)}?`, () => permaDeletePr(menu.row.id), "Deleted")}>Perma-Delete</MenuItem>}
                </Menu>
            )}
            <PrConfirmDialog
                open={!!pending}
                message={pending?.message}
                loading={pendingLoading}
                onClose={() => { if (!pendingLoading) setPending(null); }}
                onConfirm={runPending}
            />
        </>
    );
}
